const getConn = () =>
  navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;

// Measures actual RTT to the app's own origin. More reliable than browser estimates
// because it reflects real conditions, not a rolling average from past activity.
export const probeLatency = async () => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  const url = `${window.location.origin}/manifest.json?_probe=${Date.now()}`;
  const start = performance.now();
  try {
    await fetch(url, { method: 'HEAD', cache: 'no-store', signal: controller.signal });
    return Math.round(performance.now() - start);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

// Combines a measured RTT (from probeLatency) with the browser's connection API
// estimates. Measured RTT takes priority when available.
export const assessQuality = (measuredRtt) => {
  const conn = getConn();
  const rtt = measuredRtt ?? conn?.rtt ?? null;
  const downlink = conn?.downlink ?? null;
  const effectiveType = conn?.effectiveType ?? null;
  const saveData = conn?.saveData ?? false;

  if (saveData) {
    return { level: 'datasaver', color: '#faad14', rtt, downlink, effectiveType };
  }

  const isPoor =
    (rtt != null && rtt > 500) ||
    effectiveType === 'slow-2g' ||
    (downlink != null && downlink < 0.3);

  const isFair =
    !isPoor &&
    ((rtt != null && rtt > 250) ||
      effectiveType === '2g' ||
      (downlink != null && downlink < 1.5));

  const isModerate =
    !isPoor &&
    !isFair &&
    ((rtt != null && rtt > 120) ||
      effectiveType === '3g' ||
      (downlink != null && downlink < 5));

  if (isPoor) return { level: 'poor', color: '#ff4d4f', rtt, downlink, effectiveType };
  if (isFair) return { level: 'fair', color: '#ff7a45', rtt, downlink, effectiveType };
  if (isModerate) return { level: 'moderate', color: '#faad14', rtt, downlink, effectiveType };
  return { level: 'good', color: '#52c41a', rtt, downlink, effectiveType };
};

export const subscribeToNetworkChanges = (callback) => {
  const conn = getConn();
  if (!conn) return () => {};
  conn.addEventListener('change', callback);
  return () => conn.removeEventListener('change', callback);
};
